import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Loader from './Loader';
import {
  resolveLandingRoute, hasDashboardMenu, isOwnerRole, isAgencyAdminRole, AGENCY_HOME_ROUTE,
} from '../config/menuCodeMap';

/**
 * Loading Spinner Component
 */
export const LoadingSpinner = () => (
  <div className="h-screen bg-[#F5F9FF] dark:bg-[#1b1f26]">
    <Loader text="Authenticating..." />
  </div>
);

/**
 * Where a signed-in user lands.
 *
 * Decided by what they were GRANTED, not by their role: granted the Dashboard
 * module (or a manager, who always holds it) → the dashboard overview;
 * otherwise → the first item their own sidebar draws, i.e. the lowest-priority
 * module they actually have. Nobody is dropped on a page they were not given.
 *
 * See resolveLandingRoute in config/menuCodeMap.js — the same table the
 * sidebar renders from, so the two cannot disagree.
 */
export const RootRedirect = () => {
  const { user, allAvailableMenus, isMenuActive } = useAuth();

  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  const landing = resolveLandingRoute(user, allAvailableMenus, isMenuActive, { isDomiciliary });

  // Null means they hold nothing at all. The API refuses that login, so this is
  // only reachable with a stale session — send them back to sign in rather than
  // to a dashboard they have no modules for.
  if (!landing) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={landing} replace />;
};

/**
 * Gate on the manager dashboard overview.
 *
 * The dashboard is a granted module like any other now, so it cannot be
 * reached by typing the URL. Somebody without it is sent to their own landing
 * page instead of being shown a page they were never given.
 */
export const DashboardMenuRoute = ({ children }) => {
  const { user, allAvailableMenus, isMenuActive } = useAuth();

  if (hasDashboardMenu(user, allAvailableMenus, isMenuActive)) {
    return children;
  }

  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';
  const landing = resolveLandingRoute(user, allAvailableMenus, isMenuActive, { isDomiciliary });

  return <Navigate to={landing || '/login'} replace />;
};

/**
 * Pages that belong to employees only — e.g. My leave. The account owner is not
 * an employee, so they are sent to their own landing page instead.
 */
export const NotForOwnerRoute = ({ children }) => {
  const { user } = useAuth();
  if (isOwnerRole(user)) return <Navigate to="/" replace />;
  return children;
};

// Pages every workspace shares: profile, notifications, support.
const SHARED_ROUTES = ['/settings', '/notifications', '/support'];
const isUnder = (path, base) => path === base || path.startsWith(`${base}/`);

/**
 * Keeps the two workspaces apart. An agency administrator only ever sees the
 * agency workspace (/agency/...) and the shared pages; nobody else may open
 * /agency at all. A UI boundary — the API refuses the other side regardless.
 */
export const WorkspaceBoundary = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const inAgency = isUnder(pathname, '/agency');
  const shared = SHARED_ROUTES.some((base) => isUnder(pathname, base));

  if (isAgencyAdminRole(user)) {
    if (!inAgency && !shared) return <Navigate to={AGENCY_HOME_ROUTE} replace />;
  } else if (inAgency) {
    return <Navigate to="/" replace />;
  }
  return children;
};
